"use client";

/**
 * JarvisToast — single entry point for transient feedback across the app.
 *
 * Wraps `@mantine/notifications` with consistent variant-driven styling so
 * call sites stay terse:
 *
 *   JarvisToast.success("File uploaded");
 *   JarvisToast.error("Failed to save", "Network error");
 *   JarvisToast.warning("Belum bisa Submit", "Afiliasi wajib diisi");
 *
 * Each variant maps to a Mantine color + the same default Indonesian
 * fallback titles we used to type by hand, but every call still accepts an
 * explicit title or full options override.
 */

import { notifications } from "@mantine/notifications";

export type JarvisToastVariant = "success" | "error" | "warning" | "info";

type JarvisToastOptions = {
  title?: string;
  message: string;
  autoClose?: number | false;
  withCloseButton?: boolean;
  id?: string;
};

const DEFAULT_TITLES: Record<JarvisToastVariant, string> = {
  success: "Berhasil",
  error: "Gagal",
  warning: "Perhatian",
  info: "Info",
};

const VARIANT_COLORS: Record<JarvisToastVariant, string> = {
  success: "green",
  error: "red",
  warning: "yellow",
  info: "blue",
};

const show = (variant: JarvisToastVariant, opts: JarvisToastOptions) => {
  notifications.show({
    id: opts.id,
    title: opts.title ?? DEFAULT_TITLES[variant],
    message: opts.message,
    color: VARIANT_COLORS[variant],
    autoClose: opts.autoClose ?? 4000,
    withCloseButton: opts.withCloseButton ?? true,
  });
};

const make = (variant: JarvisToastVariant) =>
  function call(message: string, title?: string, extra?: Omit<JarvisToastOptions, "message" | "title">) {
    show(variant, { message, title, ...(extra ?? {}) });
  };

export const JarvisToast = {
  success: make("success"),
  error: make("error"),
  warning: make("warning"),
  info: make("info"),
  /**
   * Escape hatch when callers need full Mantine options control.
   */
  show,
};

export default JarvisToast;
